import { useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { getComparatorOffers, useQuery } from "wasp/client/operations";
import { Helmet } from "react-helmet-async";
import { SlidersHorizontal } from "lucide-react";
import useTranslation from "../client/hooks/useTranslation";
import { Button } from "../client/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "../client/components/ui/sheet";
import type { OfferVM, FirmVM, SortKey, ComparatorFilters } from "./lib/types";
import { paramsToFilters, filtersToParams } from "./lib/urlState";
import { applyFilters, defaultFilters } from "./lib/filtering";
import { sortOffers } from "./lib/sorting";
import { derivePlatforms } from "./lib/platforms";
import ComparatorResults from "./ComparatorResults";
import ComparatorFiltersPanel from "./filters/ComparatorFilters";
import ComparatorCompareBar from "./ComparatorCompareBar";
import ComparatorCompareDialog from "./ComparatorCompareDialog";
import { Link as WaspRouterLink, routes } from "wasp/client/router";

export default function ComparatorPage() {
  const t = useTranslation();
  const { data, isLoading } = useQuery(getComparatorOffers);
  const [searchParams, setSearchParams] = useSearchParams();

  const now = useMemo(() => new Date(), []);
  const filters = useMemo(() => paramsToFilters(searchParams), [searchParams]);
  const allOffers = (data?.offers ?? []) as OfferVM[];
  const firmMap = useMemo(() => {
    const m = new Map<string, FirmVM>();
    ((data?.firms ?? []) as FirmVM[]).forEach((f) => m.set(f.id, f));
    return m;
  }, [data]);
  const firmList = (data?.firms ?? []) as FirmVM[];
  const availableSizes = useMemo(
    () => [...new Set(allOffers.map((o) => o.accountSizeK))].sort((a, b) => a - b),
    [allOffers],
  );
  const availablePlatforms = useMemo(() => derivePlatforms(allOffers), [allOffers]);
  const filtered = useMemo(
    () => applyFilters(allOffers, firmMap, filters, now),
    [allOffers, firmMap, filters, now],
  );
  const sorted = useMemo(
    () => sortOffers(filtered, firmMap, filters, now),
    [filtered, firmMap, filters, now],
  );

  const updateFilters = useCallback(
    (next: ComparatorFilters) => {
      const params = filtersToParams(next);
      setSearchParams(params, { replace: true });
    },
    [setSearchParams],
  );
  const onSortChange = useCallback(
    (key: SortKey) => {
      const sameKey = filters.sortKey === key;
      updateFilters({
        ...filters,
        sortKey: key,
        sortDir: sameKey && filters.sortDir === "asc" ? "desc" : "asc",
      });
    },
    [filters, updateFilters],
  );

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [compareOpen, setCompareOpen] = useState(false);
  const onToggleCompare = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < 4) next.add(id);
      return next;
    });
  }, []);
  const selectedOffers = useMemo(
    () => allOffers.filter((o) => selectedIds.has(o.id)),
    [allOffers, selectedIds],
  );

  const filtersNode = (
    <ComparatorFiltersPanel
      filters={filters}
      firms={firmList}
      availableSizes={availableSizes}
      availablePlatforms={availablePlatforms}
      onChange={updateFilters}
      onReset={() => updateFilters(defaultFilters())}
    />
  );

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{t.comparator.metaTitle}</title>
        <meta name="description" content={t.comparator.metaDescription} />
      </Helmet>
      <div className="max-w-screen-2xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold text-foreground mb-2">
          {t.comparator.h1}
        </h1>
        <p className="text-muted-foreground mb-2 max-w-3xl">
          {t.comparator.introP1}
        </p>
        <p className="text-muted-foreground mb-8 max-w-3xl">
          {t.comparator.introP2}
        </p>

        {/* Mobile filters trigger */}
        <div className="lg:hidden mb-4">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm">
                <SlidersHorizontal className="mr-2 h-4 w-4" />
                {t.comparator.filtersButton}
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[320px] overflow-y-auto">
              {filtersNode}
            </SheetContent>
          </Sheet>
        </div>

        {isLoading ? (
          <p className="text-muted-foreground">…</p>
        ) : (
          <div className="flex gap-6">
            <aside className="hidden lg:block w-64 shrink-0">{filtersNode}</aside>
            <div className="flex-1 min-w-0">
              <ComparatorResults
                offers={sorted}
                totalCount={allOffers.length}
                firms={firmMap}
                now={now}
                filters={filters}
                onSortChange={onSortChange}
                selectedIds={selectedIds}
                onToggleCompare={onToggleCompare}
              />
            </div>
          </div>
        )}

        <div className="mt-10 rounded-lg border border-border bg-card p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-foreground font-medium">
            {t.comparator.edgelyCtaTitle}
          </p>
          <WaspRouterLink to={routes.SignupRoute.to}>
            <Button>{t.comparator.edgelyCtaButton}</Button>
          </WaspRouterLink>
        </div>
        <div className="mt-4 text-center text-sm text-muted-foreground">
          <span>{t.comparator.suggestFirmTitle}</span>{" "}
          <a
            href="mailto:contact@edgely.trade"
            className="text-primary hover:underline"
          >
            {t.comparator.suggestFirmCta}
          </a>
        </div>

        {selectedIds.size >= 2 && (
          <ComparatorCompareBar
            count={selectedIds.size}
            onOpen={() => setCompareOpen(true)}
            onReset={() => setSelectedIds(new Set())}
          />
        )}
        <ComparatorCompareDialog
          open={compareOpen}
          onOpenChange={setCompareOpen}
          offers={selectedOffers}
          firms={firmMap}
          now={now}
        />
      </div>
    </div>
  );
}
